import React, { useEffect, useState } from 'react';
import { fetchDiscordPresence, connectWebSocket, getStatusColor, getStatusText, getSpotifyInfo, getActivity } from '../utils/lanyard';
import '../styles/DiscordStatus.css';

const DiscordStatus = () => {
  const [presence, setPresence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    // Initial fetch
    const fetchInitialPresence = async () => {
      try {
        const data = await fetchDiscordPresence();
        setPresence(data);
      } catch (error) {
        console.error('Error fetching initial presence:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialPresence();

    // Set up WebSocket for real-time updates
    const handleUpdate = (data) => {
      // Use requestAnimationFrame for smoother updates
      window.requestAnimationFrame(() => {
        setPresence(data);
      });
    };

    const disconnect = connectWebSocket(handleUpdate);

    // Add multiple polling strategies for redundancy
    // 1. Fast polling every 5 seconds
    const fastInterval = setInterval(() => {
      fetchDiscordPresence().then(data => {
        if (data) handleUpdate(data);
      });
    }, 5000);

    // 2. Use visibility change to force refresh when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDiscordPresence().then(data => {
          if (data) handleUpdate(data);
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disconnect();
      clearInterval(fastInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (loading) {
    return <div className="discord-status-loading">Loading status...</div>;
  }

  if (!presence) {
    return <div className="discord-status-error">Failed to load Discord status</div>;
  }

  const statusColor = getStatusColor(presence.discord_status);
  const statusText = getStatusText(presence.discord_status);
  const spotifyInfo = getSpotifyInfo(presence);
  const activity = getActivity(presence);

  // Create avatar URL with fallback
  const getAvatarUrl = () => {
    if (!presence.discord_user || !presence.discord_user.id) {
      return `/assets/default.png`;
    }
    
    if (!presence.discord_user.avatar) {
      return `/assets/default.png`;
    }
    
    return `https://cdn.discordapp.com/avatars/${presence.discord_user.id}/${presence.discord_user.avatar}.${presence.discord_user.avatar.startsWith('a_') ? 'gif' : 'png'}?size=128`;
  };

  return (
    <div className="discord-status-container">
      <div className="discord-status-header">
        <div className="discord-avatar">
          {avatarError ? (
            <img 
              src="/assets/default.png" 
              alt="Discord Avatar" 
            />
          ) : (
            <img 
              src={getAvatarUrl()} 
              alt="Discord Avatar" 
              onError={() => setAvatarError(true)}
            />
          )}
          <div className="status-indicator" style={{ backgroundColor: statusColor }}></div>
        </div>
        <div className="discord-info">
          <div className="discord-username">{presence.discord_user.username}</div>
          {presence.discord_user.discriminator !== '0' && (
            <div className="discord-discriminator">#{presence.discord_user.discriminator}</div>
          )}
          <div className="discord-status-text">{statusText}</div>
        </div>
      </div>

      {spotifyInfo.isPlaying && (
        <div className="spotify-container">
          <div className="spotify-icon">
            <svg xmlns="http://www.w3.org/2000/svg" height="168px" width="168px" version="1.1" viewBox="0 0 168 168">
              <path fill="#1ED760" d="m83.996 0.277c-46.249 0-83.743 37.493-83.743 83.742 0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738l0.001-0.004zm38.404 120.78c-1.5 2.46-4.72 3.24-7.18 1.73-19.662-12.01-44.414-14.73-73.564-8.07-2.809 0.64-5.609-1.12-6.249-3.93-0.643-2.81 1.11-5.61 3.926-6.25 31.9-7.291 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-0.903-8.148-4.35-1.04-3.453 0.907-7.093 4.354-8.143 30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976v-0.001zm0.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219-1.254-4.14 1.08-8.513 5.221-9.771 29.581-8.98 78.756-7.245 109.83 11.202 3.73 2.209 4.95 7.016 2.74 10.733-2.2 3.722-7.02 4.949-10.73 2.739z"/>
            </svg>
          </div>
          <div className="spotify-info">
            <div className="spotify-title">Listening to Spotify</div>
            <div className="spotify-song">{spotifyInfo.song}</div>
            <div className="spotify-artist">by {spotifyInfo.artist}</div>
          </div>
          {spotifyInfo.albumArtUrl && (
            <div className="spotify-album-art">
              <img 
                src={spotifyInfo.albumArtUrl} 
                alt="Album Cover" 
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>
      )}

      {activity && (
        <div className="activity-container">
          <div className="activity-header">
            {activity.name}
          </div>
          {activity.details && (
            <div className="activity-details">{activity.details}</div>
          )}
          {activity.state && (
            <div className="activity-state">{activity.state}</div>
          )}
          {activity.assets?.large_image && (
            <div className="activity-image">
              <img 
                src={activity.assets.large_image.startsWith('mp:') 
                  ? `https://media.discordapp.net/${activity.assets.large_image.replace('mp:', '')}`
                  : `https://cdn.discordapp.com/app-assets/${activity.application_id}/${activity.assets.large_image}.png`} 
                alt={activity.assets.large_text || "Activity"} 
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscordStatus; 